import React from "react";
import { FaChalkboard, FaVideo } from "react-icons/fa";
import { RiQuestionAnswerLine } from "react-icons/ri";
import { MdOutlineAssignment } from "react-icons/md";
import { motion } from "framer-motion";

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const features: Feature[] = [
  {
    title: "Interactive Whiteboard",
    description:
      "Share lessons, annotate documents, and foster interactive discussions with our collaborative whiteboard tool.",
    icon: <FaChalkboard className="w-8 h-8" />,
    color: "indigo",
  },
  {
    title: "Q&A Sessions",
    description:
      "Foster collaborative learning with real-time Q&A features. Students ask questions and receive instant feedback.",
    icon: <RiQuestionAnswerLine className="w-8 h-8" />,
    color: "pink",
  },
  {
    title: "HD Video Conferencing",
    description:
      "Connect in real-time through high-quality video. Share screens and collaborate without disruptions.",
    icon: <FaVideo className="w-8 h-8" />,
    color: "blue",
  },
  {
    title: "Smart Assignments",
    description:
      "Create, distribute, and grade assignments seamlessly with our intuitive assignment management system.",
    icon: <MdOutlineAssignment className="w-8 h-8" />,
    color: "green",
  },
];

const colorVariants = {
  indigo: {
    bg: "bg-indigo-50",
    text: "text-indigo-600",
    hover: "group-hover:bg-indigo-100",
    shadow: "group-hover:shadow-indigo-200",
    border: "border-indigo-200",
  },
  pink: {
    bg: "bg-pink-50",
    text: "text-pink-600",
    hover: "group-hover:bg-pink-100",
    shadow: "group-hover:shadow-pink-200",
    border: "border-pink-200",
  },
  blue: {
    bg: "bg-blue-50",
    text: "text-blue-600",
    hover: "group-hover:bg-blue-100",
    shadow: "group-hover:shadow-blue-200",
    border: "border-blue-200",
  },
  green: {
    bg: "bg-green-50",
    text: "text-green-600",
    hover: "group-hover:bg-green-100",
    shadow: "group-hover:shadow-green-200",
    border: "border-green-200",
  },
};

const FeatureCard: React.FC<Feature> = ({
  title,
  description,
  icon,
  color,
}) => {
  const colors = colorVariants[color as keyof typeof colorVariants];

  return (
    <motion.div
      whileHover={{ y: -8 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`group flex-1 p-8 bg-white rounded-2xl border ${colors.border} shadow-lg hover:shadow-xl transition-all duration-300 min-w-[250px]`}>
      <div className="flex flex-col space-y-6">
        <div
          className={`p-4 ${colors.bg} ${colors.hover} rounded-xl w-16 h-16 flex items-center justify-center transition-colors duration-300`}>
          <div className={colors.text}>{icon}</div>
        </div>
        <h3 className="text-2xl font-bold text-gray-800">{title}</h3>
        <p className="text-gray-600 leading-relaxed text-base">{description}</p>
        <div className="pt-2">
          <button
            className={`flex items-center ${colors.text} font-medium text-sm hover:underline`}>
            Learn more
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4 ml-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

const CoreFunctionalities: React.FC = () => {
  return (
    <section className="relative bg-gradient-to-b from-gray-50 to-white py-24">
      {/* Background accent elements */}
      <div className="absolute top-0 right-0 -mt-16 w-64 h-64 bg-indigo-50 rounded-full opacity-70 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 -mb-16 w-80 h-80 bg-pink-50 rounded-full opacity-70 blur-3xl"></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}>
            <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase mb-3">
              CORE FEATURES
            </h2>
            <h1 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight leading-tight">
              Seamless Online{" "}
              <span className="text-indigo-600">Learning Experience</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Our platform combines powerful tools to create an engaging virtual
              classroom environment
            </p>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
          {features.map((feature, index) => (
            <FeatureCard key={index} {...feature} />
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-20 text-center">
          <button className="px-8 py-4 bg-indigo-600 text-white rounded-xl font-medium text-lg hover:bg-indigo-700 transition-colors shadow-lg hover:shadow-xl hover:shadow-indigo-200 transition-shadow duration-300">
            Explore All Features
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default CoreFunctionalities;
